// Workspace surface for the Note Taker (kind: 'notecard'). Note capture + review live HERE — in the
// chat workspace, exactly where the list/draft cards render — never a separate page. SAM drives it:
// start_note_taker opens this card and begins recording; stop_note_taker (or the Stop button) ends
// it. On stop the note is created + analysed and shown for review, and the note context is pushed to
// SAM (active_note) so a voice session can read it back and edit/convert via delegate.
//
// Voice handoff: onCaptureStart mutes the live agent mic (so dictation isn't treated as conversation)
// and onCaptureStop un-mutes it; the active_note context injection makes the agent narrate on stop.
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { CaptionBox } from '@/components/chat/CaptionBox';
import { HistoryMenu, ReplyStrip, type RecentEntry } from '@/components/chat/UXView';
import { useNoteRecorder } from '@/hooks/useNoteRecorder';
import type { Note } from '@/redux/internal-services/notesApi';
import { useAnalyzeNoteMutation, useCreateNoteMutation } from '@/redux/internal-services/notesApi';
import { NoteReviewCard } from '@/components/notes/NoteReviewCard';
import { NoteWaveRing } from '@/components/notes/NoteWaveRing';

// Module-level map so the consumed startRev value outlives any NotePanel remount
// (useRef resets on unmount; this Map does not). Keyed by note card id.
const _consumedStartRevs = new Map<string, number>();

/** A note-capture card rendered in the workspace surface (kind: 'notecard'). `startRev` is bumped
 *  to (re)start recording (SAM start_note_taker); `stopRev` is bumped to stop it (SAM
 *  stop_note_taker). `messageId` links it to the turn's assistant reply. */
export interface NoteCardMessage {
  kind: 'notecard';
  id: string;
  contextType?: string;
  entityId?: string;
  startRev?: number;
  stopRev?: number;
  messageId?: string;
}

type Phase = 'idle' | 'recording' | 'processing' | 'review' | 'error';

const MicGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
    <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z" fill="currentColor" />
    <path d="M19 11a7 7 0 0 1-14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function newDraftId(): string {
  try { return crypto.randomUUID(); } catch { return `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}

export const NotePanel = ({
  note, messageStrip, onExpandStrip, isWaiting, history = [], onReplay, historyHasMore, onHistoryLoadMore,
  onClose, onCaptureStart, onCaptureStop, onActiveNote, onOpenTask, stopWord,
  onExecuteFollowupsRef, onToggleFollowupsRef, onExpandFollowupRef, onNotesListChanged,
}: {
  note: NoteCardMessage;
  messageStrip: string;
  onExpandStrip?: () => void;
  /** True while SAM is composing a new reply for this turn — shows the typing-dots strip. */
  isWaiting?: boolean;
  history?: RecentEntry[];
  onReplay?: (e: RecentEntry) => void;
  historyHasMore?: boolean;
  onHistoryLoadMore?: () => void;
  /** Remove the note card from the workspace. */
  onClose: () => void;
  /** Spoken phrase that ends dictation hands-free (chat_settings.note_stop_word); undefined = off. */
  stopWord?: string;
  /** Recording started — pause the live voice agent (voice sessions only). */
  onCaptureStart?: () => void;
  /** Recording stopped/cancelled — resume the live voice agent if it was paused for this capture.
   *  Pass the freshly-analysed note when available so it can be narrated on the resumed session. */
  onCaptureStop?: (note?: Note | null) => void;
  /** Surface the on-screen note to SAM (active_note), or null to clear it. */
  onActiveNote?: (note: Note | null) => void;
  /** Open an already-created follow-up record inline in the workspace (no modal). */
  onOpenTask?: (taskId: string) => void;
  /** Caller sets this ref so SAM's EXECUTE_NOTE_FOLLOWUPS action can trigger the review card's
   *  "Create all selected" batch without a direct prop dependency on NoteReviewCard. */
  onExecuteFollowupsRef?: MutableRefObject<(() => void) | null>;
  /** Caller sets this ref so SAM's SET_NOTE_FOLLOWUP_INCLUDED action can check/uncheck one,
   *  several, or all follow-up suggestions on the review card. */
  onToggleFollowupsRef?: MutableRefObject<((opts: { suggestionId?: string; suggestionIds?: string[]; included: boolean }) => void) | null>;
  /** Caller sets this ref so SAM's EXPAND_NOTE_FOLLOWUP action can expand a follow-up's
   *  review/edit panel (optionally prefilling fields) without creating anything. */
  onExpandFollowupRef?: MutableRefObject<((opts: { suggestionId: string; fields?: Record<string, unknown> }) => void) | null>;
  /** A note was deleted/saved/converted from the review card — refresh the on-screen notes list
   *  card (if any) so a discarded note doesn't linger when the user navigates back to it. */
  onNotesListChanged?: () => void;
}) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [reviewNote, setReviewNote] = useState<Note | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [procStage, setProcStage] = useState<'transcribing' | 'analyzing' | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const procTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftIdRef = useRef<string>('');

  const [createNote] = useCreateNoteMutation();
  const [analyzeNote] = useAnalyzeNoteMutation();

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);
  useEffect(() => () => stopTimer(), [stopTimer]);

  // Transcription timeout — if STT doesn't resolve within 45 s, bail out with an error
  // instead of leaving the user staring at the spinner indefinitely.
  useEffect(() => {
    if (phase !== 'processing') {
      if (procTimeoutRef.current) { clearTimeout(procTimeoutRef.current); procTimeoutRef.current = null; }
      return;
    }
    procTimeoutRef.current = setTimeout(() => {
      setErrorMsg(t('noteTaker.states.timeout', 'Transcription timed out — please try again.'));
      setPhase('error');
      setProcStage(null);
      onCaptureStop?.();
    }, 45_000);
    return () => {
      if (procTimeoutRef.current) { clearTimeout(procTimeoutRef.current); procTimeoutRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const recorder = useNoteRecorder({
    stopWord,
    onFinal: async ({ transcript, audioUrl }) => {
      console.info('[NotePanel] onFinal | chars=%d hasAudio=%s', transcript.length, !!audioUrl);
      setProcStage('analyzing');
      try {
        const created = await createNote({
          source: 'voice',
          final_transcript: transcript,
          clean_transcript: transcript,
          audio_url: audioUrl || undefined,
          ...(note.contextType && note.entityId
            ? { context: { context_type: note.contextType, entity_id: note.entityId } }
            : {}),
          idempotency_key: draftIdRef.current,
        }).unwrap();
        console.info('[NotePanel] createNote OK — running analysis before showing review | note_id=%s', created.note_id);
        // Run analysis before showing the review card so the user sees everything at once
        // (summary, tags, follow-ups). If analysis fails we still show the card with the raw
        // transcript — the user can re-analyze manually.
        let noteToShow = created;
        try {
          noteToShow = await analyzeNote(created.note_id).unwrap();
          console.info('[NotePanel] analysis done | note_id=%s', created.note_id);
        } catch (e) {
          console.warn('[NotePanel] analysis failed — showing raw transcript | note_id=%s', created.note_id, e);
        }
        setReviewNote(noteToShow);
        setProcStage(null);
        setPhase('review');
        onActiveNote?.(noteToShow);
        onCaptureStop?.(noteToShow);
      } catch (e) {
        onCaptureStop?.();
        console.error('[NotePanel] createNote failed on stop:', e);
        const msg = (e as { data?: { message?: string } })?.data?.message || t('noteTaker.states.failed', 'Something went wrong.');
        setErrorMsg(msg);
        setPhase('error');
        setProcStage(null);
      }
    },
  });

  const startVoice = useCallback(async () => {
    setErrorMsg(null);
    try {
      const id = newDraftId();
      draftIdRef.current = id;
      onCaptureStart?.();
      await recorder.start(id);
      setElapsed(0);
      stopTimer();
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      setPhase('recording');
    } catch {
      onCaptureStop?.();
      setErrorMsg(t('noteTaker.states.failed', 'Something went wrong.'));
      setPhase('error');
    }
  }, [recorder, stopTimer, t, onCaptureStart, onCaptureStop]);

  const stopVoice = useCallback(() => {
    stopTimer();
    recorder.stop();
    setProcStage('transcribing');
    setPhase('processing');
  }, [recorder, stopTimer]);

  const cancelVoice = useCallback(() => {
    stopTimer();
    recorder.cancel();
    onCaptureStop?.();
    onActiveNote?.(null);
    _consumedStartRevs.delete(note.id);
    onClose();
  }, [recorder, stopTimer, onCaptureStop, onActiveNote, onClose, note.id]);

  // Start recording when SAM opens the card (startRev bumped). Runs once per bump.
  // lastStartRef is initialised from the module-level map so a NotePanel remount
  // (e.g. user opens a task then presses Back, or toggles hands-free mode) does NOT
  // re-trigger recording — only a genuine new startRev bump from SAM does.
  const lastStartRef = useRef<number | undefined>(_consumedStartRevs.get(note.id));
  // Keep a stable ref to startVoice so the effect dep array stays [startRev, phase]
  // and doesn't re-fire on every render (startVoice recreates because recorder is a
  // new object literal each render even though its inner callbacks are stable).
  const startVoiceRef = useRef(startVoice);
  useEffect(() => { startVoiceRef.current = startVoice; });
  useEffect(() => {
    if (note.startRev === undefined || note.startRev === lastStartRef.current) return;
    lastStartRef.current = note.startRev;
    _consumedStartRevs.set(note.id, note.startRev);
    // Always start fresh — cancel any in-progress capture and clear review state
    if (phase === 'recording') recorder.cancel();
    stopTimer();
    setReviewNote(null);
    setProcStage(null);
    void startVoiceRef.current();
  }, [note.startRev, note.id, phase, recorder, stopTimer]);

  // Stop recording when SAM says stop (stopRev bumped) while we're recording.
  const lastStopRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (note.stopRev === undefined || note.stopRev === lastStopRef.current) return;
    lastStopRef.current = note.stopRev;
    if (phase === 'recording') stopVoice();
  }, [note.stopRev, phase, stopVoice]);

  const done = useCallback(() => {
    onActiveNote?.(null);
    _consumedStartRevs.delete(note.id);
    onClose();
  }, [onActiveNote, onClose, note.id]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <ReplyStrip text={messageStrip} onExpand={onExpandStrip} isWaiting={isWaiting} />
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-1.5 border-b border-ui-border">
        <span className="text-xs font-semibold uppercase tracking-wide text-ui-text2 truncate">
          {phase === 'recording' ? t('noteTaker.states.recording', 'Recording')
            : phase === 'processing' ? t('noteTaker.pipeline.analyzing', 'Analyzing')
            : phase === 'review' ? t('noteTaker.reviewTitle', 'Review Note')
            : t('noteTaker.title', 'Note Taker')}
        </span>
        <HistoryMenu history={history} onReplay={onReplay} hasMore={historyHasMore} onLoadMore={onHistoryLoadMore} />
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar px-3 py-3 min-h-0">
        {phase === 'review' && reviewNote ? (
          <NoteReviewCard
            note={reviewNote}
            onDiscard={done}
            onSaved={(n) => onActiveNote?.(n)}
            onNoteSynced={(n) => onActiveNote?.(n)}
            onDone={done}
            onOpenTask={onOpenTask}
            onExecuteFollowupsRef={onExecuteFollowupsRef}
            onToggleFollowupsRef={onToggleFollowupsRef}
            onExpandFollowupRef={onExpandFollowupRef}
            onNotesListChanged={onNotesListChanged}
          />
        ) : phase === 'recording' ? (
          <div className="relative overflow-hidden rounded-2xl border border-brand-bright/30 bg-ui-card p-6 shadow-sm">
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center gap-2 self-end text-xs font-medium uppercase tracking-widest text-brand-light">
                <span className="inline-flex h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" aria-hidden />
                {t('noteTaker.states.recording', 'Recording')}
              </div>
              <div className="relative flex w-full items-center justify-center">
                <NoteWaveRing levelRef={recorder.levelRef} active height={140} />
                <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand-dark text-white ring-1 ring-brand-bright/50 shadow-lg">
                  <MicGlyph className="h-6 w-6" />
                </span>
              </div>
              <div className="font-mono text-3xl font-semibold tracking-widest text-ui-text tabular-nums">{formatTime(elapsed)}</div>
              <div className="min-h-[44px] w-full max-w-md text-center">
                <CaptionBox text={recorder.interim || t('noteTaker.listening', 'Listening…')} show tone="user" />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={cancelVoice}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-ui-border text-ui-text3 transition hover:bg-ui-hover"
                  aria-label={t('noteTaker.cancel', 'Cancel')}
                  title={t('noteTaker.cancel', 'Cancel')}
                >
                  ✕
                </button>
                <button
                  type="button"
                  onClick={stopVoice}
                  className="flex items-center gap-2 rounded-full bg-brand-dark px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:brightness-110"
                >
                  <span className="h-3.5 w-3.5 rounded-[3px] bg-white" aria-hidden />
                  {t('noteTaker.stopRecording', 'Stop')}
                </button>
              </div>
            </div>
          </div>
        ) : phase === 'processing' ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-ui-border bg-ui-card px-8 py-10 text-center">
            <span className="h-9 w-9 animate-spin rounded-full border-[3px] border-brand-bright/25 border-t-brand-bright" aria-hidden />
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-ui-text">
                {procStage === 'transcribing' ? t('noteTaker.pipeline.transcribing', 'Transcribing…') : t('noteTaker.pipeline.analyzingActive', 'Analyzing your note…')}
              </p>
              <p className="text-xs text-ui-text3">{t('noteTaker.analyzingHelp', "We're identifying summaries, follow-ups and actions.")}</p>
            </div>
          </div>
        ) : (
          // idle / error — the recording is agent-driven, so this is only shown briefly (or on error).
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-ui-border bg-ui-card px-6 py-8 text-center">
            {phase === 'error' && <p className="text-sm text-red-600">{errorMsg || t('noteTaker.states.failed', 'Something went wrong.')}</p>}
            <button
              type="button"
              onClick={startVoice}
              className="group relative flex items-center justify-center transition hover:opacity-90"
              aria-label={t('noteTaker.startRecording', 'Start recording')}
              title={t('noteTaker.startRecording', 'Start recording')}
            >
              <NoteWaveRing levelRef={recorder.levelRef} active={false} height={110} />
              <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand-dark text-white ring-1 ring-brand-bright/50 shadow-lg transition group-hover:scale-105">
                <MicGlyph className="h-5 w-5" />
              </span>
            </button>
            <span className="text-xs font-medium text-ui-text3">{t('noteTaker.startRecording', 'Start recording')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotePanel;
